import React from 'react'
import { useMatches, useNavigate, useSearchParams } from 'react-router-dom'
import { PubstackRouteMeta } from '~/auth/ProtectedRoute'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { BreadcrumbProps, Breadcrumbs } from '~/components/Breadcrumbs'
import { useNodeStaffAccess } from '~/hooks/useNodeStaffAccess'

/**
 * `withSubtitle: false` for pages that stand in for the route rather than being it. The subtitle is
 * route metadata describing the real page — "Create and manage rules that will be used in stacks"
 * above an upgrade wall is an instruction for something the reader cannot do. The trail itself stays,
 * since it still says which part of the product this is.
 */
export const useBreadcrumbs = <T,>(additionalParams?: T, { withSubtitle = true }: { withSubtitle?: boolean } = {}) => {
  const navigate = useNavigate()
  const [search] = useSearchParams()
  const matches = useMatches()
  const user = useAuthenticatedUser()
  const { hasNode } = useNodeStaffAccess()

  const breadcrumbsMatches = matches.filter((match) => {
    return !!(match.handle as PubstackRouteMeta).breadcrumbLabel || !!(match.handle as PubstackRouteMeta).breadcrumbSubtitle
  })

  const breadcrumbs: BreadcrumbProps[] = breadcrumbsMatches
    .map((match, index, full) => {
      let label: string | React.ReactNode = ''
      const metaData = match.handle as PubstackRouteMeta<T>
      if (metaData) {
        const breadcrumbLabel = metaData.breadcrumbLabel
        if (breadcrumbLabel) {
          if (typeof breadcrumbLabel === 'string' || React.isValidElement(breadcrumbLabel)) {
            label = breadcrumbLabel
          } else if (typeof breadcrumbLabel === 'function') {
            label = breadcrumbLabel({ search, additionalParams })
          }
        }
      }
      return {
        label,
        route:
          full.length - 1 !== index
            ? {
                pathname: match.pathname,
                search: search.toString(),
              }
            : undefined,
      }
    })
    .filter(({ label }) => !!label)

  const breadcrumbSubtitle = (breadcrumbsMatches.pop()?.handle as PubstackRouteMeta<T>)?.breadcrumbSubtitle
  const breadcrumbSubtitleComputed =
    breadcrumbSubtitle && (typeof breadcrumbSubtitle === 'function' ? breadcrumbSubtitle({ search, additionalParams, user: user ?? undefined, hasNode }) : breadcrumbSubtitle)
  return <Breadcrumbs data={breadcrumbs} subtitle={withSubtitle ? breadcrumbSubtitleComputed : undefined} onClick={({ route }) => route && navigate(route)} />
}
